import React, { createContext, useContext, useEffect } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import api from '../api';
import logger from './../utils/logger.js';

// Microsoft Auth Configuration
const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID || '',
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: window.location.origin + '/login'
  }
};

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Create context for Microsoft auth
const MicrosoftAuthContext = createContext();

export const useMicrosoftAuth = () => useContext(MicrosoftAuthContext);

export const MicrosoftAuthProvider = ({ children }) => {
  // Check for broken Microsoft redirect on initial load
  useEffect(() => {
    const handleBrokenRedirect = () => {
      const url = window.location.href;
      const pathname = window.location.pathname;
      const search = window.location.search;
      
      // Check for multiple Microsoft login error conditions
      const isLoginError = 
        // 1. Error in URL with specific message
        (search.includes('error=') && search.includes('No%20user%20account%20was%20found')) ||
        // 2. A broken Microsoft error path that doesn't exist
        (pathname.includes('/login') && pathname.endsWith('error') && !pathname.includes('/login/microsoft/error')) ||
        // 3. Direct access to the Microsoft error URL
        (pathname === '/login/microsoft/error' && window.history.length <= 2);
      
      // Skip if we're already on the correct login page with error parameter
      if (pathname === '/login' && search.includes('error=')) {
        logger.log("Already on login page with error parameter");
        return;
      }
      
      if (isLoginError) {
        logger.error("Detected broken Microsoft login redirect, redirecting to login page");
        
        // Store the fact we've redirected to prevent loops
        const hasRedirected = sessionStorage.getItem('ms_redirect_handled');
        if (hasRedirected) {
          logger.error("Already redirected once, not redirecting again to prevent loop");
          // Clear the flag after a few seconds
          setTimeout(() => sessionStorage.removeItem('ms_redirect_handled'), 5000);
          return;
        }
        
        // Set flag that we've redirected
        sessionStorage.setItem('ms_redirect_handled', 'true');
        
        // Redirect to login with error
        const errorMessage = "No user account was found with your Microsoft email address. Please contact your administrator.";
        window.location.href = `/login?error=${encodeURIComponent(errorMessage)}`;
      }
    };
    
    // Handle redirect immediately
    handleBrokenRedirect();
    
    // Also add on route change handling
    const handleRouteChange = () => {
      setTimeout(handleBrokenRedirect, 100);
    };
    
    // Listen for navigation events
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  // Function to initiate Microsoft login
  const loginWithMicrosoft = async () => {
    try {
      // Check if we need to force a new login (from URL parameter)
      const urlParams = new URLSearchParams(window.location.search);
      const forceNewLogin = urlParams.has('force_new_login');
      
      // Get auth URL from the backend
      const response = await api.get('/auth/microsoft');
      
      if (response.data.success && response.data.auth_url) {
        // If force new login is requested, add prompt=login parameter to auth URL
        let authUrl = response.data.auth_url;
        
        if (forceNewLogin) {
          logger.log("Force new login requested, adding prompt=login parameter");
          // Add prompt=login parameter to force Microsoft to show account picker
          authUrl += (authUrl.includes('?') ? '&' : '?') + 'prompt=login';
        }
        
        // Redirect to Microsoft login page
        window.location.href = authUrl;
        return { success: true };
      } else {
        logger.error('Failed to get Microsoft auth URL:', response.data);
        return { success: false, error: 'Failed to get Microsoft auth URL' };
      }
    } catch (error) {
      logger.error('Error during Microsoft login initialization:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Error connecting to authentication service'
      };
    }
  };

  // Function to check Microsoft login status after redirect
  const checkMicrosoftLoginStatus = async (reason) => {
    try {
      const response = await api.get(`/api/auth/microsoft/status${reason ? `?reason=${reason}` : ''}`);
      return response.data;
    } catch (error) {
      logger.error('Error checking Microsoft login status:', error);
      return { 
        success: false, 
        logged_in: false,
        error: error.response?.data?.message || 'Error checking login status'
      };
    }
  };

  // Context value
  const contextValue = {
    loginWithMicrosoft,
    checkMicrosoftLoginStatus
  };

  return (
    <MsalProvider instance={msalInstance}>
      <MicrosoftAuthContext.Provider value={contextValue}>
        {children}
      </MicrosoftAuthContext.Provider>
    </MsalProvider>
  );
}; 